import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, Card, CardContent
} from '@mui/material';
import { Add, Edit, Delete, ViewModule, ViewList, Email, Phone } from '@mui/icons-material';

const Clients: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', status: 'Active' });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'firstName',
    sortOrder: 'asc' as 'asc' | 'desc'
  });

  useEffect(() => {
    // Start with empty list - require user to search
    // fetchClients(); // Commented out to prevent loading all clients
  }, []);
  
  useEffect(() => {
    // Only fetch when user searches
    if (filters.search.length >= 2) {
      fetchClients(1, 50, filters.search);
    } else if (filters.search.length === 0) {
      setClients([]);
      setFilteredClients([]);
    }
  }, [filters.search]);

  const applyFilters = () => {
    let filtered = clients;

    if (filters.search) {
      filtered = filtered.filter(client => 
        `${client.firstName} ${client.lastName}`.toLowerCase().includes(filters.search.toLowerCase()) ||
        client.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        client.phone.includes(filters.search)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(client => client.status === filters.status);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let result = 0;
      switch (filters.sortBy) {
        case 'firstName':
          result = a.firstName.localeCompare(b.firstName);
          break;
        case 'lastName':
          result = a.lastName.localeCompare(b.lastName);
          break;
        case 'email':
          result = a.email.localeCompare(b.email);
          break;
        case 'joinDate':
          result = new Date(a.joinDate).getTime() - new Date(b.joinDate).getTime();
          break;
        default:
          return 0;
      }
      return filters.sortOrder === 'desc' ? -result : result;
    });

    setFilteredClients(sorted);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, clients]);

  const fetchClients = async (page = 1, limit = 50, search = '') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search })
      });
      
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/clients?${params}`);
      if (response.ok) {
        const result = await response.json();
        const users = result.clients || result; // Handle both paginated and non-paginated responses
        const clientData = users.map((user: any) => ({
          id: user.id,
          firstName: user.first_name,
          lastName: user.last_name,
          email: user.email,
          phone: user.phone || '+1-555-0000',
          status: user.is_active ? 'Active' : 'Inactive',
          joinDate: user.created_at ? new Date(user.created_at).toLocaleDateString() : new Date().toLocaleDateString()
        }));
        
        if (page === 1) {
          setClients(clientData);
          setFilteredClients(clientData);
        } else {
          setClients(prev => [...prev, ...clientData]);
          setFilteredClients(prev => [...prev, ...clientData]);
        }
      } else {
        // Fallback to mock data if API fails
        const mockClients = [
          { id: 1, firstName: 'Sarah', lastName: 'Johnson', email: 'sarah.johnson@email.com', phone: '555-0101', status: 'Active', joinDate: '2024-01-15' },
          { id: 2, firstName: 'Emily', lastName: 'Davis', email: 'emily.davis@email.com', phone: '555-0102', status: 'Active', joinDate: '2024-02-20' },
          { id: 3, firstName: 'Maria', lastName: 'Garcia', email: 'maria.garcia@email.com', phone: '555-0103', status: 'Active', joinDate: '2024-03-10' },
          { id: 4, firstName: 'Jessica', lastName: 'Wilson', email: 'jessica.wilson@email.com', phone: '555-0104', status: 'Active', joinDate: '2024-04-05' }
        ];
        setClients(mockClients);
        setFilteredClients(mockClients);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      // Fallback to mock data on error
      const mockClients = [
        { id: 1, firstName: 'Sarah', lastName: 'Johnson', email: 'sarah.johnson@email.com', phone: '555-0101', status: 'Active', joinDate: '2024-01-15' },
        { id: 2, firstName: 'Emily', lastName: 'Davis', email: 'emily.davis@email.com', phone: '555-0102', status: 'Active', joinDate: '2024-02-20' },
        { id: 3, firstName: 'Maria', lastName: 'Garcia', email: 'maria.garcia@email.com', phone: '555-0103', status: 'Active', joinDate: '2024-03-10' },
        { id: 4, firstName: 'Jessica', lastName: 'Wilson', email: 'jessica.wilson@email.com', phone: '555-0104', status: 'Active', joinDate: '2024-04-05' }
      ];
      setClients(mockClients);
      setFilteredClients(mockClients);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingClient ? `https://salon.rockyroadsolutions.com/api/clients/${editingClient.id}` : 'https://salon.rockyroadsolutions.com/api/clients';
      const method = editingClient ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        fetchClients();
        setIsFormOpen(false);
        setEditingClient(null);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', status: 'Active' });
      }
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleEdit = (client: any) => {
    setEditingClient(client);
    setFormData(client);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await fetch(`https://salon.rockyroadsolutions.com/api/clients/${id}`, { method: 'DELETE' });
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Clients</Typography>
        <Box display="flex" gap={2}>
          <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}>
            <IconButton size="small" onClick={() => setViewMode('grid')} sx={{ color: viewMode === 'grid' ? '#E91E63' : '#666' }}>
              <ViewModule />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('list')} sx={{ color: viewMode === 'list' ? '#E91E63' : '#666' }}>
              <ViewList />
            </IconButton>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setIsFormOpen(true)}>
            Add Client
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Clients</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              size="small"
              placeholder="Search by name, email, or phone"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Sort By"
              value={filters.sortBy}
              onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
              size="small"
            >
              <MenuItem value="firstName">First Name</MenuItem>
              <MenuItem value="lastName">Last Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="joinDate">Join Date</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <TextField
              select
              fullWidth
              label="Order"
              value={filters.sortOrder}
              onChange={(e) => setFilters({...filters, sortOrder: e.target.value as 'asc' | 'desc'})}
              size="small"
            >
              <MenuItem value="asc">↑ Asc</MenuItem>
              <MenuItem value="desc">↓ Desc</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => setFilters({ search: '', status: '', sortBy: 'firstName', sortOrder: 'asc' })}
            size="small"
          >
            Clear Filters
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 2, color: 'text.secondary' }}>
            Showing {filteredClients.length} of {clients.length} clients
          </Typography>
        </Box>
      </Paper>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredClients.map((client) => (
            <Grid item xs={12} md={6} lg={4} key={client.id}>
              <Card sx={{
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: '1px solid #E91E63',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 25px rgba(233, 30, 99, 0.2)' }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}>
                      {client.firstName?.[0]}{client.lastName?.[0]}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {client.firstName} {client.lastName}
                      </Typography>
                      <Chip label={client.status} color={client.status === 'Active' ? 'success' : 'default'} size="small" />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Email fontSize="small" /> {client.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" /> {client.phone}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(client)} sx={{ borderColor: '#FF9800', color: '#FF9800' }}>Edit</Button>
                    <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(client.id)} sx={{ borderColor: '#F44336', color: '#F44336' }}>Delete</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {filteredClients.map((client) => (
            <Card key={client.id} sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: '1px solid #E91E63',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: '0 4px 15px rgba(233, 30, 99, 0.15)' }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {client.firstName?.[0]}{client.lastName?.[0]}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1" fontWeight={600}>
                      {client.firstName} {client.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      {client.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body2" color="text.secondary">
                      {client.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Chip label={client.status} color={client.status === 'Active' ? 'success' : 'default'} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(client)} sx={{ borderColor: '#FF9800', color: '#FF9800', minWidth: 'auto', px: 1 }}>Edit</Button>
                      <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(client.id)} sx={{ borderColor: '#F44336', color: '#F44336', minWidth: 'auto', px: 1 }}>Del</Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="First Name" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Last Name" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Status" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingClient ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
